/**
 * Typed API surface — mirror image of the backend's `app/api/v1/router.py`.
 *
 * Grouped by domain module so a frontend feature maps to exactly one backend module.
 * Types are handwritten for now; when the contract churns, generate them from
 * `/api/openapi.json` instead of letting the two drift.
 */

import { apiRequest } from './client'

// ── system ────────────────────────────────────────────────────────────────────────

export interface HealthResponse {
  ok: boolean
  service: string
  env: string
  version: string
}

export interface ReadyResponse extends HealthResponse {
  database: string
}

// ── auth ──────────────────────────────────────────────────────────────────────────

export interface Principal {
  kind: 'machine' | 'human'
  subject: string
}

export interface LoginResponse {
  access_token: string
  token_type: string
  expires_in: number
}

// ── customers ─────────────────────────────────────────────────────────────────────

export interface Customer {
  id: string
  code: string
  name: string
  legal_name: string | null
  country: string
  currency: string
  email: string | null
  phone: string | null
  notes: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CustomerPage {
  items: Customer[]
  total: number
  limit: number
  offset: number
}

export interface CustomerInput {
  code: string
  name: string
  country?: string
  currency?: string
  email?: string | null
  phone?: string | null
  notes?: string | null
  is_active?: boolean
}

export interface CustomerListQuery {
  limit?: number
  offset?: number
  search?: string
  activeOnly?: boolean
}

// ── endpoints ─────────────────────────────────────────────────────────────────────

export const system = {
  health: () => apiRequest<HealthResponse>('/health'),
  ready: () => apiRequest<ReadyResponse>('/ready'),
}

export const auth = {
  login: (username: string, password: string) =>
    // anonymous: the login call itself must not carry a stale token, or a broken session
    // turns "log in again" into a 401 that clears the very session being created.
    apiRequest<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { username, password },
      anonymous: true,
    }),

  me: () => apiRequest<Principal>('/auth/me'),
}

export const customers = {
  list: (query: CustomerListQuery = {}) => {
    const params = new URLSearchParams()
    params.set('limit', String(query.limit ?? 50))
    params.set('offset', String(query.offset ?? 0))
    if (query.search) params.set('search', query.search)
    if (query.activeOnly) params.set('active_only', 'true')
    return apiRequest<CustomerPage>(`/customers?${params.toString()}`)
  },

  get: (id: string) => apiRequest<Customer>(`/customers/${id}`),

  create: (input: CustomerInput) =>
    apiRequest<Customer>('/customers', { method: 'POST', body: input }),

  update: (id: string, patch: Partial<CustomerInput>) =>
    apiRequest<Customer>(`/customers/${id}`, { method: 'PATCH', body: patch }),

  remove: (id: string) => apiRequest<void>(`/customers/${id}`, { method: 'DELETE' }),
}
